/**
 * src/lib/teamLogTags.ts
 *
 * Single source of truth for the Team Log's resolution-vocabulary tag
 * strings. Before this file, "resolved", "blocker-resolved", "reply-to:",
 * "pre-flight-approved" etc. were independently spelled out as raw string
 * literals across at least 6 files (service.ts, ActionsTab.tsx, teamLog.ts,
 * useAttentionCount.ts, SprintBoardTab.tsx, RecoveryTab.tsx) with no shared
 * declaration — which is how teamLog.ts's own FINDING_GATE_TRIGGER_TAGS
 * ended up listing "preflight" (no hyphen) while every real pre-flight post
 * and every other reader in the codebase uses "pre-flight" (with hyphen),
 * silently breaking the §3-D Finding Gate's auto-BLOCKED trigger for
 * pre-flight posts specifically — the same fix also adds
 * "peer-review-request" to that trigger list, a real documented convention
 * (server/api/skills.ts's Peer Review Delegation Protocol) that was missing
 * entirely.
 *
 * Phase 1 of a broader tag-protocol consolidation — only the blocker/
 * finding-gate cluster is migrated so far; the deferred remainder is
 * tracked separately. This repo also already has two other independent
 * "known tags" lists for unrelated purposes: src/lib/tagColors.ts's
 * KNOWN_TAGS (UI pill-color lookup) and src/lib/tagRegistry.ts's
 * STANDARD_TAGS (composer `#`-picker autocomplete). Deliberately not
 * merged into this file — they solve display/UX problems, not resolution
 * semantics, and unifying them is a separate decision this pass doesn't make.
 */

export const TAG = {
  BLOCKER: "blocker",
  RESOLVED: "resolved",
  BLOCKER_RESOLVED: "blocker-resolved",
  REQUIRES_RESPONSE: "requires-response",
  FINDING: "finding",
  FINDING_ACKNOWLEDGED: "finding-acknowledged",
  PRE_FLIGHT: "pre-flight",
  PRE_FLIGHT_APPROVED: "pre-flight-approved",
  // adk-relay/deterministic_scheduler.py:418-432 — "preflight-approved" (no
  // hyphen) is a legacy misspelling the scheduler's own
  // _FINDING_GATE_UNBLOCK_TAGS still honors for historical posts. Do not
  // write this tag on new posts; it's kept here only so recognized-tag
  // checks (e.g. tagCorruptionWarning.ts's GATE_UNBLOCK_TAGS) don't flag it.
  PRE_FLIGHT_APPROVED_LEGACY: "preflight-approved",
  PRE_FLIGHT_DISMISSED: "pre-flight-dismissed",
  PRE_FLIGHT_BLOCKED: "pre-flight-blocked",
  PEER_REVIEW_REQUEST: "peer-review-request",
} as const;

const REPLY_TO_PREFIX = "reply-to:";

/** Builds a `reply-to:{seq}` tag. */
export function replyToTag(seq: number): string {
  return `${REPLY_TO_PREFIX}${seq}`;
}

/**
 * Parses a `reply-to:{seq}` tag into its target sequence number, or null if
 * `tag` isn't a reply-to tag or the sequence isn't a finite number.
 */
export function parseReplyToTag(tag: string): number | null {
  if (!tag.startsWith(REPLY_TO_PREFIX)) return null;
  const seq = Number(tag.slice(REPLY_TO_PREFIX.length));
  return Number.isFinite(seq) ? seq : null;
}

/** Every `reply-to:{seq}` target found in a tags array. */
export function replyToSeqs(tags: readonly string[] | undefined): number[] {
  if (!tags) return [];
  const seqs: number[] = [];
  for (const tag of tags) {
    const seq = parseReplyToTag(tag);
    if (seq !== null) seqs.push(seq);
  }
  return seqs;
}

// ─── Tag glossary (track:backlog-tags, spec #55907) ─────────────────────────
//
// One list of the tags that carry meaning, with a one-line explanation and a
// group. Exact entries match a whole tag; entries with `prefix: true` match
// every tag starting with the entry's `tag` (e.g. "project:"). `reserved`
// marks tags only the system writes: save_spec_to_backlog and
// update_backlog_tags refuse them. This explains tags; it does not change what
// any of them does in the scheduler (see docs/team-log-tag-contract.md).

export type TagGroup = "tier" | "lifecycle" | "scope" | "topic";

export interface TagGlossaryEntry {
  tag: string;
  prefix?: boolean;
  group: TagGroup;
  meaning: string;
  reserved?: boolean;
}

export const TAG_GLOSSARY: readonly TagGlossaryEntry[] = [
  { tag: "tier:now", group: "tier", meaning: "Do this now: it can be put on the next sprint." },
  { tag: "tier:soon", group: "tier", meaning: "Do this soon, after the work marked now." },
  { tag: "tier:later", group: "tier", meaning: "Not planned yet: parked until it moves up." },
  { tag: "backlog", group: "lifecycle", reserved: true, meaning: "This post is a backlog item." },
  { tag: "spec-validated", group: "lifecycle", reserved: true, meaning: "The spec passed validation, so the item can be boarded." },
  { tag: "spec-validated:warned", group: "lifecycle", reserved: true, meaning: "The spec passed validation with warnings." },
  { tag: "backlog-resolved", group: "lifecycle", reserved: true, meaning: "Closes a backlog item; written when its work is done." },
  { tag: "backlog-acknowledged", group: "lifecycle", reserved: true, meaning: "Older way of closing a backlog item; still read, no longer written." },
  { tag: "resolved", group: "lifecycle", reserved: true, meaning: "Older way of closing a post; still read, no longer written." },
  { tag: "archived", group: "lifecycle", reserved: true, meaning: "Hidden from the backlog list." },
  { tag: TAG.BLOCKER, group: "lifecycle", reserved: true, meaning: "Work is stopped and needs someone to act." },
  { tag: TAG.BLOCKER_RESOLVED, group: "lifecycle", reserved: true, meaning: "Closes a blocker." },
  { tag: TAG.FINDING, group: "lifecycle", reserved: true, meaning: "Something found that needs a person's acknowledgement before the track continues." },
  { tag: TAG.FINDING_ACKNOWLEDGED, group: "lifecycle", reserved: true, meaning: "A person acknowledged a finding." },
  { tag: TAG.PRE_FLIGHT, group: "lifecycle", reserved: true, meaning: "A builder's pre-flight check before starting a track." },
  { tag: TAG.PRE_FLIGHT_APPROVED, group: "lifecycle", reserved: true, meaning: "A pre-flight check was approved." },
  { tag: TAG.PRE_FLIGHT_APPROVED_LEGACY, group: "lifecycle", reserved: true, meaning: "Misspelt older form of pre-flight-approved; still read, never written." },
  { tag: TAG.PRE_FLIGHT_DISMISSED, group: "lifecycle", reserved: true, meaning: "A pre-flight check was dismissed." },
  { tag: TAG.PRE_FLIGHT_BLOCKED, group: "lifecycle", reserved: true, meaning: "A pre-flight check found a problem." },
  { tag: TAG.PEER_REVIEW_REQUEST, group: "lifecycle", reserved: true, meaning: "Asks another agent to review." },
  { tag: TAG.REQUIRES_RESPONSE, group: "lifecycle", reserved: true, meaning: "Holds the track until a person replies." },
  { tag: "requires-human-response", group: "lifecycle", reserved: true, meaning: "A stop notice that needs a person's reply." },
  { tag: "blocked", group: "lifecycle", reserved: true, meaning: "Marks a post that a stop notice is anchored to." },
  { tag: "agent-died", group: "lifecycle", reserved: true, meaning: "Relay's notice that an agent session ended unexpectedly." },
  { tag: "finding-gate-held", group: "lifecycle", reserved: true, meaning: "Relay's notice that a track is held waiting for a finding to be acknowledged." },
  { tag: "session-start", group: "lifecycle", reserved: true, meaning: "An agent started a session." },
  { tag: "session-end", group: "lifecycle", reserved: true, meaning: "An agent ended a session." },
  { tag: "status", group: "lifecycle", reserved: true, meaning: "A progress update; it gates nothing." },
  { tag: "nudge", group: "lifecycle", reserved: true, meaning: "A reminder sent to an agent." },
  { tag: "peer-review-approved", group: "lifecycle", reserved: true, meaning: "A peer reviewer approved." },
  { tag: "review-approved-with-notes", group: "lifecycle", reserved: true, meaning: "A reviewer approved with notes." },
  { tag: "approved", group: "lifecycle", reserved: true, meaning: "A person's approval; only people write it." },
  { tag: "directive", group: "lifecycle", reserved: true, meaning: "A person's instruction to the team; only people write it." },
  { tag: "build-complete", group: "lifecycle", reserved: true, meaning: "A builder finished a track and opened a pull request." },
  { tag: "build-stopped", group: "lifecycle", reserved: true, meaning: "A builder could not finish and needs a person." },
  { tag: "review-approved", group: "lifecycle", reserved: true, meaning: "A reviewer approved the pull request." },
  { tag: "review-rejected", group: "lifecycle", reserved: true, meaning: "A reviewer sent the pull request back." },
  { tag: "review-carried-over", group: "lifecycle", reserved: true, meaning: "Relay's notice that the new head has the same changes as the reviewed commit (only main moved or it was rebased), so the earlier approval carries to it." },
  { tag: "investigation-complete", group: "lifecycle", reserved: true, meaning: "An investigation track posted its report." },
  { tag: "sprint-carry-forward", group: "lifecycle", reserved: true, meaning: "Carried over from an earlier sprint." },
  { tag: "high-authorized", group: "lifecycle", reserved: true, meaning: "A person authorized HIGH-size work." },
  { tag: "critical-authorized", group: "lifecycle", reserved: true, meaning: "A person authorized CRITICAL-size work." },
  { tag: "agent:", prefix: true, group: "scope", reserved: true, meaning: "Which agent wrote the post." },
  { tag: "project:", prefix: true, group: "scope", reserved: true, meaning: "Which project the item belongs to." },
  { tag: "feature:", prefix: true, group: "scope", reserved: true, meaning: "Which feature of the project the item belongs to." },
  { tag: "outcome:", prefix: true, group: "scope", reserved: true, meaning: "Which key outcome of the feature the item covers." },
  { tag: "blast:", prefix: true, group: "scope", reserved: true, meaning: "How big a change this is, from low to critical; decides who must approve it." },
  { tag: "track:", prefix: true, group: "scope", reserved: true, meaning: "The track (one unit of build work) the post belongs to." },
  { tag: "origin-track:", prefix: true, group: "scope", reserved: true, meaning: "The track that wrote the spec this item came from." },
  { tag: "track_type:", prefix: true, group: "scope", reserved: true, meaning: "Whether the track builds code or only investigates." },
  { tag: "sprint:", prefix: true, group: "scope", reserved: true, meaning: "Which sprint the post belongs to." },
  { tag: "reply-to:", prefix: true, group: "scope", reserved: true, meaning: "The post this one answers." },
  { tag: "tier-was:", prefix: true, group: "scope", reserved: true, meaning: "The tier an item had before it was changed." },
  { tag: "design-ref", group: "topic", meaning: "A designer's mockup for one screen: one self-contained HTML mockup in an html block. The latest per project and screen:<id> is the current design." },
  { tag: "screen:", prefix: true, group: "scope", meaning: "Which screen of the project a design-ref post is the mockup for." },
  { tag: "run:", prefix: true, group: "scope", reserved: true, meaning: "Which visual check run a post belongs to." },
  { tag: "visual-check-started", group: "lifecycle", reserved: true, meaning: "Relay started a visual check of a project's screens." },
  { tag: "visual-check-result", group: "lifecycle", reserved: true, meaning: "The result of a visual check: each screen as built, next to its design." },
  { tag: "future-feature", group: "topic", meaning: "An idea for later, not work to schedule now." },
  { tag: "idea", group: "topic", meaning: "A rough idea that has not been specced." },
  { tag: "bug", group: "topic", meaning: "Something that works wrongly." },
  { tag: "security", group: "topic", meaning: "Concerns security or access." },
  { tag: "performance", group: "topic", meaning: "Concerns speed or cost." },
  { tag: "tech-debt", group: "topic", meaning: "Cleanup of existing code, with no new behaviour." },
  { tag: "docs", group: "topic", meaning: "Concerns documentation or explanatory text." },
  { tag: "ui", group: "topic", meaning: "Changes what people see on screen." },
];

/** The glossary entry for `tag`, or undefined when Relay has no stated meaning for it. */
export function lookupTag(tag: string): TagGlossaryEntry | undefined {
  return (
    TAG_GLOSSARY.find((e) => !e.prefix && e.tag === tag) ??
    TAG_GLOSSARY.find((e) => e.prefix && tag.startsWith(e.tag))
  );
}

/** True for tags only the system writes; tools that take caller tags refuse these. */
export function isReservedTag(tag: string): boolean {
  return lookupTag(tag)?.reserved === true;
}

const TIER_TAGS = ["tier:now", "tier:soon", "tier:later"] as const;
const NOT_NOW_TAGS = ["future-feature", "idea"] as const;

/**
 * Plain-words warnings for tags that disagree: more than one tier, or an
 * urgent tier (now/soon) beside a tag that says the work is for later.
 * Advisory only; callers never refuse on these.
 */
export function tagConflicts(tags: readonly string[]): string[] {
  const warnings: string[] = [];
  const tiers = TIER_TAGS.filter((t) => tags.includes(t));
  for (let i = 0; i < tiers.length; i++) {
    for (let j = i + 1; j < tiers.length; j++) {
      warnings.push(`${tiers[i]} and ${tiers[j]} disagree: an item has one tier.`);
    }
  }
  for (const urgent of ["tier:now", "tier:soon"]) {
    if (!tags.includes(urgent)) continue;
    for (const later of NOT_NOW_TAGS) {
      if (tags.includes(later)) {
        warnings.push(`${urgent} and ${later} disagree: ${later} says this is not work for now.`);
      }
    }
  }
  return warnings;
}
